/* eslint-disable complexity */
import { MutationType } from '@tencent/slide-sdk-pb-model';
import { MutationSource } from '../constants/mutation-constants';
import { createMutations } from '../mutation/mutation-factory';
import { record } from '../utils/recorder';
import { OperationType } from '../constants';
import { PresentationEventTypes } from './event-manager/event-types';
import { MonitorStatus } from './logger.interface';
import { createTransactionProvider, type ITransactionProvider } from './event-manager/transaction-events';
import type { ILogger } from './logger.interface';
import type { IEventManager } from './event-manager';
import type { IPresentationDataModel, PresentationDataModel } from '../model/presentation-data-model';
import type { Mutation } from '../mutation/base/base-mutation';

type MutationSegment = {
    source: MutationSource;
    startIndex: number;
    endIndex: number;
};

export interface IMutationManager {
    /**
     * 是否暂停提交 Mutation
     */
    isSubmitSuspended: boolean;
    /**
     * 将 Mutation 加入队列，等待批量应用
     */
    enqueue(mutations: Mutation[], source: MutationSource): IMutationManager;
    /**
     * 应用队列中暂存的 Mutations，并为每个 mutation 生成 InvertedMutations
     */
    apply(options?: { inversion?: boolean; transaction?: boolean }): Mutation[];
    /**
     * 提交已经应用过的 Mutation，如果 MutationSource 为 Editor，
     * 则会将事务内的 Mutations 封装为 Command，再提交到 CommandManager
     */
    commit(): IMutationManager;
    /**
     * 暂停提交 Mutation（不落盘），直到调用 resumeSubmit() 时，才会提交 Mutation
     */
    suspendSubmit(): IMutationManager;
    /**
     * 恢复提交 Mutation
     */
    resumeSubmit(): IMutationManager;
    /**
     * 监听 Mutation Commit，一般在 Mutation 事务结束时触发。
     *
     * 场景：
     *   * DataModel 的一系列 API 调用，最外层事务时触发；
     *   * Undo / Redo 结束后触发；
     *   * 收到协同消息应用之后触发；
     *   * 离线编辑应用之后触发；
     */
    onCommit(callback: () => void): () => void;
    /**
     * 注册 PresentationDataModel，必须
     * @param presentation
     */
    registerPresentation(presentation: IPresentationDataModel): void;
    /**
     * 获取 Mutation 的 InvertedMutations
     * @param mutation
     */
    getInvertedMutation(mutation: Mutation): Mutation[];
}

export class MutationManager implements IMutationManager {
    public isSubmitSuspended = false;

    private mutations: Mutation[] = [];
    private invertedMutations = new WeakMap<Mutation, Mutation[]>();
    private segments: MutationSegment[] = [];
    private applied = 0;
    private fatalError?: Error;
    private presentation: PresentationDataModel;
    private transaction: ITransactionProvider;

    private submitSuspendedMutations: [Mutation[], MutationSource][] = [];

    public constructor(
        private events: IEventManager,
        private logger?: ILogger,
        private getDocInfoProvider?: () => { globalPadId?: string },
    ) {
        this.transaction = createTransactionProvider(this.events);
        this.init();
    }

    public registerPresentation(presentation: IPresentationDataModel) {
        this.presentation = presentation as PresentationDataModel;
        return this;
    }

    @record(OperationType.EnqueueMutations)
    public enqueue(mutations: Mutation[], source: MutationSource) {
        for (const mutation of mutations) {
            this.mutations.push(mutation);
        }

        const lastSegment = this.segments[this.segments.length - 1];
        if (lastSegment && lastSegment.source === source) {
            lastSegment.endIndex += mutations.length;
        } else {
            this.segments.push({
                source,
                startIndex: this.mutations.length - mutations.length,
                endIndex: this.mutations.length,
            });
        }

        return this;
    }

    public apply = (options?: { inversion?: boolean; transaction?: boolean }): Mutation[] => {
        if (!this.presentation) {
            this.fatalError = new Error('Presentation is not registered');
        }

        if (this.fatalError) {
            throw new Error(`unable to apply new mutations after fatal error: ${this.fatalError.message}`);
        }

        const invertedMutations: Mutation[][] = [];

        // Apply Mutation 前必须实例化所有的子 DataModel，否则查找 Model 会报错
        this.presentation.populate();

        const action = options?.transaction ? this.transaction : (action: () => void) => action();
        action(() => {
            for (const { source, endIndex } of this.segments) {
                if (this.applied >= endIndex) {
                    continue;
                }

                this.events.emit(PresentationEventTypes.MutationSource, source);

                while (this.applied < endIndex) {
                    const mutation = this.mutations[this.applied];
                    const err = mutation.check(this.presentation, source);
                    if (err) {
                        this.emitError(err, 'check', source, mutation.type, this.applied);
                        throw err;
                    }

                    if (this.canCommitCommand(source) || this.canInvertCommand(source) || options?.inversion) {
                        const inverted = this.invert(mutation);
                        this.invertedMutations.set(mutation, inverted);
                        invertedMutations.push(inverted);
                    }

                    try {
                        mutation.apply(this.presentation);
                    } catch (err) {
                        this.emitError(err as Error, 'apply', source, mutation.type, this.applied);
                        throw err;
                    }
                    this.applied += 1;
                }
            }
        });

        return invertedMutations.reverse().flat();
    };

    public commit = () => {
        for (const { source, startIndex, endIndex } of this.segments) {
            const mutations = this.mutations.slice(startIndex, endIndex);
            if (this.canCommitCommand(source)) {
                this.events.emit(
                    PresentationEventTypes.CommitCommand,
                    mutations,
                    mutations.map((mutation) => this.invertedMutations.get(mutation) ?? []),
                    source,
                );
                if (source === MutationSource.Editor) {
                    this.events.emit(PresentationEventTypes.Edit);
                }
            }

            if (this.canSubmitCommand(source)) {
                if (this.isSubmitSuspended) {
                    this.submitSuspendedMutations.push([mutations, source]);
                } else {
                    this.events.emit(PresentationEventTypes.SubmitMutations, { mutations, source });
                }
            }

            // 通知 Mutation 已经应用
            this.events.emit(PresentationEventTypes.MutationsApplied, source);
        }

        this.reset();

        return this;
    };

    public suspendSubmit = () => {
        this.isSubmitSuspended = true;

        return this;
    };

    public resumeSubmit = () => {
        this.isSubmitSuspended = false;

        for (const [mutations, source] of this.submitSuspendedMutations) {
            this.events.emit(PresentationEventTypes.SubmitMutations, { mutations, source });
        }
        this.submitSuspendedMutations = [];

        return this;
    };

    public onCommit(callback: () => void): () => void {
        return this.events.on(PresentationEventTypes.CommitTransaction, callback);
    }

    /**
     * @deprecated use .apply({ inversion: true, transaction: true }}) instead
     */
    public getInvertedMutation(mutation: Mutation): Mutation[] {
        return this.invertedMutations.get(mutation) ?? [];
    }

    private init() {
        this.events.on(
            PresentationEventTypes.EnqueueMutations,
            (mutations: Mutation[], source: MutationSource) => !this.fatalError && this.enqueue(mutations, source),
        );
        this.events.on(PresentationEventTypes.EndTransaction, () => !this.fatalError && this.apply());
        this.events.on(PresentationEventTypes.CommitTransaction, () => !this.fatalError && this.commit());

        return this;
    }

    private emitError(
        error: Error,
        stage: 'check' | 'apply',
        source: MutationSource,
        type: MutationType,
        index: number,
    ) {
        error.message = `${source}@${stage}-${index} ${MutationType[type]}: ${error.message}`;
        this.logger?.error?.('[MM]', error.message);
        this.fatalError = error;
        this.reset();
        const reportId =
            this.logger?.getPrototype?.().report('CHANGESET_REPORT', {
                query: {
                    startTs: Date.now() - 24 * 60 * 60 * 1000,
                    endTs: Date.now(),
                },
            })?.value ?? '';
        // 可以移动到统一的 Reporter 里
        this.logger?.monitor?.reportStatus?.(`sdk_client_${stage}_changeset`, MonitorStatus.ERROR, error.toString(), [
            stage,
            source,
            String(index),
            MutationType[type],
            reportId,
            this.getDocInfoProvider?.()?.globalPadId ?? '',
        ]);
        this.events.emit(PresentationEventTypes.MutationError, error, stage, source, type, index);
    }

    private reset() {
        this.segments = [];
        this.mutations = [];
        this.invertedMutations = new WeakMap();
        this.applied = 0;
    }

    private canCommitCommand(source: MutationSource): boolean {
        return [MutationSource.Editor, MutationSource.Collaborator, MutationSource.CollaboratorConflict].includes(
            source,
        );
    }

    private canSubmitCommand(source: MutationSource): boolean {
        return [MutationSource.Editor, MutationSource.UndoRedo].includes(source);
    }

    /**
     * @deprecated
     */
    private canInvertCommand(source: MutationSource) {
        return [MutationSource.UndoRedo].includes(source);
    }

    private invert(mutation: Mutation): Mutation[] {
        if (!mutation.invertible) {
            return [];
        }

        return createMutations(mutation.invert(this.presentation));
    }
}
